import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search, Shield, ShieldOff, KeyRound, X, Save, UserCog, Phone, AlertTriangle,
} from "lucide-react";
import api from "@/api";
import TopBar from "@/components/TopBar";
import { useLang } from "@/context/LanguageContext";
import { toast } from "sonner";

// Apr 2026 — Super Admin "User PIN Control"
//   Search by name/phone → Set PIN / Reset to default / Block / Unblock.
//   All actions audit-logged via existing /users endpoints.

const DEFAULT_PIN = "1234";
const ROLES = [
  { id: "all",       en: "All",         ta: "அனைத்தும்" },
  { id: "salesman",  en: "Salesmen",    ta: "சேல்ஸ்மென்" },
  { id: "manager",   en: "Managers",    ta: "மேனேஜர்கள்" },
  { id: "ops_admin", en: "Ops Admin",   ta: "Ops Admin" },
  { id: "management",en: "Management",  ta: "நிர்வாகம்" },
];

export default function AdminPinControl() {
  const { lang } = useLang();
  const nav = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [editing, setEditing] = useState(null);

  const reload = () => {
    setLoading(true);
    api.get("/users", { params: { status: "all" } })
       .then(r => setUsers(r.data || []))
       .finally(() => setLoading(false));
  };
  useEffect(reload, []);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users.filter(u => {
      // Only show staff roles (not retailers/wholesalers)
      if (!["salesman", "manager", "ops_admin", "management", "super_admin"].includes(u.role)) return false;
      if (roleFilter !== "all" && u.role !== roleFilter) return false;
      if (!q) return true;
      return [u.name, u.name_ta, u.phone].filter(Boolean).join(" ").toLowerCase().includes(q);
    });
  }, [users, search, roleFilter]);

  const roleLabel = (role) => ({
    salesman:   lang === "ta" ? "சேல்ஸ்மேன்" : "Salesman",
    manager:    lang === "ta" ? "மேனேஜர்"    : "Manager",
    ops_admin:  "Ops Admin",
    management: lang === "ta" ? "நிர்வாகம்"  : "Management",
    super_admin:"Super Admin",
  }[role] || role);

  const roleColor = (role) => ({
    salesman:    "bg-[#FF9500]",
    manager:     "bg-[#5856D6]",
    ops_admin:   "bg-[#0055FF]",
    management:  "bg-[#34C759]",
    super_admin: "bg-[#FF3B30]",
  }[role] || "bg-gray-400");

  const toggleBlock = async (u) => {
    const willBlock = u.active !== false;
    const msg = willBlock
      ? (lang === "ta" ? `${u.name} ஐ தடு செய்யவா?` : `Block ${u.name}?\nThey will be unable to log in until unblocked.`)
      : (lang === "ta" ? `${u.name} ஐ மீண்டும் அனுமதிக்கவா?` : `Unblock ${u.name}?`);
    if (!window.confirm(msg)) return;
    try {
      if (willBlock) {
        await api.post(`/users/${u.id}/block`, { reason: "Super Admin action" });
      } else {
        await api.post(`/users/${u.id}/unblock`);
      }
      toast.success(willBlock ? (lang === "ta" ? "தடுக்கப்பட்டது" : "Blocked") : (lang === "ta" ? "அனுமதிக்கப்பட்டது" : "Unblocked"));
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F5F7] page-slide-in pb-12">
      <TopBar title={lang === "ta" ? "PIN கட்டுப்பாடு" : "User PIN Control"} onBack={() => nav(-1)} />
      <div className="max-w-5xl mx-auto p-4 sm:p-6 space-y-4 fade-up">
        {/* Search + Role Chips */}
        <div className="bg-white rounded-2xl p-4 soft-shadow space-y-3" data-testid="pin-toolbar">
          <div className="flex items-center gap-2 bg-[#F5F5F7] rounded-xl px-3 py-2.5">
            <Search className="w-4 h-4 text-[#86868B]" />
            <input value={search} onChange={e => setSearch(e.target.value)} data-testid="pin-search"
              placeholder={lang === "ta" ? "பெயர் / தொலைபேசி தேடு…" : "Search name or phone…"}
              className="bg-transparent flex-1 outline-none text-sm font-semibold" />
          </div>
          <div className="flex gap-1.5 flex-wrap">
            {ROLES.map(r => (
              <button key={r.id} onClick={() => setRoleFilter(r.id)} data-testid={`pin-filter-${r.id}`}
                className={`px-3 py-1.5 rounded-xl text-xs font-bold ${roleFilter === r.id ? "bg-[#0055FF] text-white" : "bg-[#F5F5F7] text-[#1D1D1F]"}`}>
                {lang === "ta" ? r.ta : r.en}
              </button>
            ))}
          </div>
        </div>

        {/* User list */}
        <div className="bg-white rounded-2xl soft-shadow overflow-hidden" data-testid="pin-user-list">
          {loading ? <div className="p-8 text-center text-[#86868B]">Loading…</div> :
           visible.length === 0 ? <div className="p-8 text-center text-[#86868B]" data-testid="pin-empty">{lang === "ta" ? "பயனர் இல்ல" : "No users match"}</div> :
           visible.map(u => {
            const blocked = u.active === false;
            return (
              <div key={u.id} className="p-4 border-b last:border-b-0 border-gray-100 flex items-center gap-3 hover:bg-gray-50/60" data-testid={`pin-row-${u.id}`}>
                <div className={`w-10 h-10 rounded-full grid place-items-center text-white text-sm font-bold shrink-0 ${roleColor(u.role)}`}>
                  {(u.name || "?")[0]}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-[#1D1D1F] truncate">{(lang === "ta" && u.name_ta) ? u.name_ta : u.name}</div>
                  <div className="text-xs text-[#86868B] flex items-center gap-2 flex-wrap">
                    <span className="font-mono">{u.phone}</span>
                    <span>·</span>
                    <span>{roleLabel(u.role)}</span>
                    {blocked && <span className="pill bg-red-50 text-red-700 font-bold uppercase tracking-wider">{lang === "ta" ? "தடுக்கப்பட்டது" : "Blocked"}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <button onClick={() => setEditing(u)} data-testid={`pin-edit-${u.id}`}
                    className="p-2 rounded-xl bg-blue-50 hover:bg-blue-100 text-[#0055FF]" title={lang === "ta" ? "PIN" : "Set PIN"}>
                    <KeyRound className="w-4 h-4" />
                  </button>
                  <button onClick={() => toggleBlock(u)} data-testid={`pin-toggle-block-${u.id}`}
                    className={`p-2 rounded-xl ${blocked ? "bg-green-50 hover:bg-green-100 text-[#34C759]" : "bg-red-50 hover:bg-red-100 text-[#FF3B30]"}`}
                    title={blocked ? "Unblock" : "Block"}>
                    {blocked ? <Shield className="w-4 h-4" /> : <ShieldOff className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-3 text-xs text-amber-900 flex gap-2" data-testid="pin-notice">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          {lang === "ta"
            ? "எல்லா மாற்றங்களும் audit log-ல் பதிவாகும். தடுக்கப்பட்ட பயனர்கள் லாக்-இன் செய்ய முடியாது."
            : "All PIN and block actions are audit-logged. Blocked users cannot log in until unblocked."}
        </div>
      </div>

      {editing && <PinModal user={editing} lang={lang} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); reload(); }} />}
    </div>
  );
}

const PinModal = ({ user, lang, onClose, onSaved }) => {
  const [pin, setPin] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  const setCustom = async () => {
    if (pin.length !== 4 || !/^\d{4}$/.test(pin)) return toast.error(lang === "ta" ? "4-இலக்க PIN தேவை" : "4-digit PIN required");
    if (pin !== confirm) return toast.error(lang === "ta" ? "PIN பொருந்தல" : "PINs don't match");
    setSaving(true);
    try {
      await api.put(`/users/${user.id}`, { pin });
      toast.success(lang === "ta" ? "PIN அமைக்கப்பட்டது" : "PIN updated");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  };

  const resetDefault = async () => {
    const msg = lang === "ta" ? `PIN-ஐ ${DEFAULT_PIN}-க்கு ரீசெட் செய்யவா?` : `Reset PIN to ${DEFAULT_PIN}?\nUser will be asked to change it.`;
    if (!window.confirm(msg)) return;
    setSaving(true);
    try {
      await api.put(`/users/${user.id}`, { pin: DEFAULT_PIN });
      toast.success(lang === "ta" ? `PIN ரீசெட் ${DEFAULT_PIN}` : `PIN reset to ${DEFAULT_PIN}`);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 grid place-items-center p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl max-w-md w-full soft-shadow-md fade-up" onClick={e => e.stopPropagation()} data-testid="pin-modal">
        <div className="p-5 border-b border-gray-100 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-[#86868B] font-bold">{lang === "ta" ? "PIN கட்டுப்பாடு" : "PIN Control"}</div>
            <h3 className="heading-font font-bold text-xl">{(lang === "ta" && user.name_ta) ? user.name_ta : user.name}</h3>
            <div className="text-xs text-[#86868B] mt-1 flex items-center gap-1.5"><Phone className="w-3 h-3" /> {user.phone}</div>
          </div>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-gray-100" data-testid="pin-modal-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#86868B] font-bold block mb-1">{lang === "ta" ? "புதிய PIN" : "New PIN"}</label>
            <input type="password" inputMode="numeric" maxLength={4} value={pin} onChange={e => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
              data-testid="pin-input-new"
              className="w-full bg-[#F5F5F7] rounded-xl px-3 py-3 text-center text-2xl tracking-[0.5em] font-bold" placeholder="••••" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#86868B] font-bold block mb-1">{lang === "ta" ? "மீண்டும் உள்ளிடு" : "Confirm PIN"}</label>
            <input type="password" inputMode="numeric" maxLength={4} value={confirm} onChange={e => setConfirm(e.target.value.replace(/\D/g, "").slice(0, 4))}
              data-testid="pin-input-confirm"
              className="w-full bg-[#F5F5F7] rounded-xl px-3 py-3 text-center text-2xl tracking-[0.5em] font-bold" placeholder="••••" />
          </div>
          <button onClick={setCustom} disabled={saving || pin.length !== 4 || pin !== confirm} data-testid="pin-save-btn"
            className="w-full bg-[#0055FF] hover:bg-[#0044CC] disabled:opacity-50 text-white rounded-xl py-3 font-bold flex items-center justify-center gap-2">
            <Save className="w-4 h-4" /> {saving ? "…" : (lang === "ta" ? "PIN சேமி" : "Save New PIN")}
          </button>
          <div className="relative py-1">
            <div className="absolute inset-x-0 top-1/2 h-px bg-gray-200" />
            <div className="relative text-center"><span className="bg-white px-3 text-[10px] uppercase text-[#86868B] font-bold">{lang === "ta" ? "அல்லது" : "or"}</span></div>
          </div>
          <button onClick={resetDefault} disabled={saving} data-testid="pin-reset-btn"
            className="w-full bg-white border border-gray-200 hover:bg-gray-50 disabled:opacity-50 text-[#FF9500] rounded-xl py-3 font-bold flex items-center justify-center gap-2">
            <UserCog className="w-4 h-4" /> {lang === "ta" ? `${DEFAULT_PIN}-க்கு ரீசெட்` : `Reset to ${DEFAULT_PIN}`}
          </button>
        </div>
      </div>
    </div>
  );
};
